import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from "react";
import { soundManager } from "@/lib/sounds";

interface KaiContextType {
  isVisible: boolean;
  isExpanded: boolean;
  customName: string;
  soundEnabled: boolean;
  setIsVisible: (visible: boolean) => void;
  setIsExpanded: (expanded: boolean) => void;
  setCustomName: (name: string) => void;
  setSoundEnabled: (enabled: boolean) => void;
  playAppearSound: () => void;
  playClickSound: () => void;
}

const KaiContext = createContext<KaiContextType | null>(null);

// Singleton flag to prevent multiple providers
let providerMounted = false;

export const KaiProvider = ({ children }: { children: ReactNode }) => {
  const [isVisible, setIsVisibleState] = useState(() => {
    if (typeof window === 'undefined') return true;
    const setting = localStorage.getItem("kai_ai_visible");
    return setting !== "false";
  });
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [customName, setCustomNameState] = useState(() => {
    if (typeof window === 'undefined') return "Kai";
    return localStorage.getItem("kai_custom_name") || "Kai";
  });

  // Sound enabled state - DEFAULT OFF
  const [soundEnabled, setSoundEnabledState] = useState(() => {
    if (typeof window === 'undefined') return false;
    const setting = localStorage.getItem("kai_sound_enabled");
    return setting === "true"; // Default is false (OFF)
  });

  const hasPlayedAppearSound = useRef(false);

  // Ensure singleton pattern
  useEffect(() => {
    if (providerMounted) {
      console.warn("KaiProvider is already mounted. Only one instance should exist.");
    }
    providerMounted = true;
    return () => {
      providerMounted = false;
    };
  }, []);

  // Sync with localStorage
  useEffect(() => {
    const handleStorage = () => {
      const setting = localStorage.getItem("kai_ai_visible");
      setIsVisibleState(setting !== "false");
      
      const name = localStorage.getItem("kai_custom_name");
      if (name) setCustomNameState(name);

      const sound = localStorage.getItem("kai_sound_enabled");
      setSoundEnabledState(sound === "true");
    };
    
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setIsVisible = useCallback((visible: boolean) => {
    setIsVisibleState(visible);
    localStorage.setItem("kai_ai_visible", visible ? "true" : "false");
    window.dispatchEvent(new StorageEvent("storage"));
  }, []);

  const setCustomName = useCallback((name: string) => {
    setCustomNameState(name);
    localStorage.setItem("kai_custom_name", name);
    window.dispatchEvent(new StorageEvent("storage"));
  }, []);

  const setSoundEnabled = useCallback((enabled: boolean) => {
    setSoundEnabledState(enabled);
    localStorage.setItem("kai_sound_enabled", enabled ? "true" : "false");
    window.dispatchEvent(new StorageEvent("storage"));
  }, []);

  const playAppearSound = useCallback(() => {
    if (!soundEnabled) return; // Respect sound setting
    if (!hasPlayedAppearSound.current) {
      hasPlayedAppearSound.current = true;
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const now = ctx.currentTime;
        
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.15);
        
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        
        osc.start(now);
        osc.stop(now + 0.25);
      } catch (e) {
        // Audio not available
      }
    }
  }, [soundEnabled]);

  const playClickSound = useCallback(() => {
    if (!soundEnabled) return; // Respect sound setting
    soundManager.playClick();
  }, [soundEnabled]);

  return (
    <KaiContext.Provider
      value={{
        isVisible,
        isExpanded,
        customName,
        soundEnabled,
        setIsVisible,
        setIsExpanded,
        setCustomName,
        setSoundEnabled,
        playAppearSound,
        playClickSound,
      }}
    >
      {children}
    </KaiContext.Provider>
  );
};

export const useKai = () => {
  const context = useContext(KaiContext);
  if (!context) {
    throw new Error("useKai must be used within a KaiProvider");
  }
  return context;
};

// Hook for components that might not be in the provider
export const useKaiSafe = () => {
  return useContext(KaiContext);
};
